import { indexes } from '@mab-common/utils'

import {
  buildArtistFacet,
  buildElasticAggsToBuckets,
  buildElasticAggsToHits,
  buildElasticToHits,
  buildFormatFacet,
  buildGenreFacet,
  buildMappedElasticHits,
  buildPriceFacet,
} from '~/services/elastic/builder'

import type { SearchSuggestion } from './types/search'
import { buildArticleArtistFacet, buildArticleCategoryFacet } from './builders'

const { RecordTypes } = indexes

export function processSuggestions(
  hits: any[] = [],
  recordType: indexes.RecordTypes,
  highlightFields: string[],
  textFormatter: (source: any) => string
): SearchSuggestion[] {
  return hits.slice(0, 3).reduce<SearchSuggestion[]>((acc, hit) => {
    const highlightText = highlightFields
      .map(field => hit.highlight?.[field]?.[0])
      .find(highlight => !!highlight)

    if (highlightText) {
      const originalText = textFormatter(hit._source)
      const plainText = highlightText.replace(/<\/?strong>/g, '')
      const startIndex = originalText
        .toLowerCase()
        .indexOf(plainText.toLowerCase())

      if (startIndex !== -1) {
        const beforeHighlight = originalText.slice(0, startIndex)
        const afterHighlight = originalText.slice(startIndex + plainText.length)
        const combinedText = beforeHighlight + highlightText + afterHighlight

        acc.push({
          text: combinedText,
          highlightedText: highlightText,
          type: recordType,
          score: hit._score || 0,
        })
      } else {
        acc.push({
          text: originalText,
          highlightedText: highlightText,
          type: recordType,
          score: hit._score || 0,
        })
      }
    }
    return acc
  }, [])
}

// export function processSearchResults(
//   data: any[] | undefined,
//   recordType: indexes.RecordTypes
// ) {
//   if (!data) return []
//   return data.flatMap(d => {
//     let aggsOrHits = buildElasticAggsToHits(d?.[0])
//     if (!aggsOrHits.length) {
//       aggsOrHits = buildElasticToHits(recordType, d?.[0])
//     }
//     return buildMappedElasticHits(aggsOrHits)
//   })
// }
export function processSearchResults(
  data: any[] | undefined,
  recordType: indexes.RecordTypes
) {
  if (!data) return { items: [], facets: null }

  // For artwork type, extract both items and facets
  if (recordType === RecordTypes.ARTWORK) {
    // Process items from ALL pages, not just the first one
    const items = data.flatMap(pageData => {
      return buildMappedElasticHits(
        buildElasticToHits(recordType, pageData?.[0])
      )
    })

    // Facets should only come from the first page
    let facets = null
    if (data[0]?.length >= 5) {
      facets = {
        artist:
          buildElasticAggsToHits(data[0]?.[1])?.map(buildArtistFacet)?.[0]
            ?.facets || [],
        price:
          buildElasticAggsToBuckets(data[0]?.[2])?.map(buildPriceFacet)?.[0]
            ?.facets || [],
        format:
          buildElasticAggsToHits(data[0]?.[3])?.map(buildFormatFacet)?.[0]
            ?.facets || [],
        genre:
          buildElasticAggsToHits(data[0]?.[4])?.map(buildGenreFacet)?.[0]
            ?.facets || [],
      }
    }

    return { items, facets }
  }

  if (
    recordType === RecordTypes.ARTICLES ||
    recordType === RecordTypes.PODCASTS_AND_VIDEOS ||
    recordType === RecordTypes.PODCASTS ||
    recordType === RecordTypes.VIDEOS
  ) {
    // Process items from ALL pages
    const items = data.flatMap(pageData => {
      return buildMappedElasticHits(
        buildElasticToHits(recordType, pageData?.[0])
      )
    })

    // Facets from first page only
    let facets = null
    if (data[0]?.length >= 3) {
      const artistAggs = data[0]?.[1]?.aggregations?.artist?.buckets
      const categoryAggs = data[0]?.[2]?.aggregations?.category?.buckets

      if (artistAggs || categoryAggs) {
        facets = {
          artist: artistAggs
            ? buildArticleArtistFacet({
                buckets: artistAggs,
              })?.facets || []
            : [],
          category: categoryAggs
            ? buildArticleCategoryFacet({
                buckets: categoryAggs,
              })?.facets || []
            : [],
        }
      }
    }

    return { items, facets }
  }

  // For other record types, keep the existing behavior (which already uses flatMap)
  const items = data.flatMap(d => {
    let aggsOrHits = buildElasticAggsToHits(d?.[0])
    if (!aggsOrHits.length) {
      aggsOrHits = buildElasticToHits(recordType, d?.[0])
    }
    return buildMappedElasticHits(aggsOrHits)
  })

  return { items, facets: null }
}

export function processInstaSearchResults(data: any[] | undefined) {
  if (!data) return []
  return data.flatMap(d => {
    return buildMappedElasticHits([
      ...buildElasticToHits(RecordTypes.ARTIST, d?.[0]),
      ...buildElasticToHits(RecordTypes.COLLECTION, d?.[1]),
      ...buildElasticToHits(RecordTypes.ARTWORK, d?.[2]),
      ...buildElasticToHits(RecordTypes.ARTICLES, d?.[3]),
      ...buildElasticToHits(RecordTypes.PODCASTS_AND_VIDEOS, d?.[4]),
    ])
  })
}

export function rankSuggestions(suggestions: SearchSuggestion[]) {
  const typePriorities: Record<indexes.RecordTypes, number> = {
    [RecordTypes.ARTIST]: 8,
    [RecordTypes.ARTWORK]: 8,
    [RecordTypes.COLLECTION]: 0,
    [RecordTypes.ARTICLES]: 3,
    [RecordTypes.PODCASTS_AND_VIDEOS]: 3,
    [RecordTypes.PODCASTS]: 3,
    [RecordTypes.VIDEOS]: 3,
    [RecordTypes.LATEST_ARTWORK]: 0,
    [RecordTypes.RELATED_ARTWORK]: 0,
    [RecordTypes.ARTICLES_INDEX]: 0,
    [RecordTypes.PROFILE_IMAGE]: 0,
    [RecordTypes.TICKETS]: 0,
    [RecordTypes.ARTIST_SELECT_LIST]: 0,
    [RecordTypes.ARTWORK_SELECT_LIST]: 0,
    [RecordTypes.ALL_ARTWORK_SELECT_LIST]: 0,
  }

  // Process scores for normalization
  if (suggestions.length > 0) {
    // Group suggestions by type for normalization within each index
    const suggestionsByType = suggestions.reduce<
      Record<string, SearchSuggestion[]>
    >((acc, suggestion) => {
      const type = suggestion.type
      if (!acc[type]) acc[type] = []
      acc[type].push(suggestion)
      return acc
    }, {})

    // Normalize scores within each type
    Object.entries(suggestionsByType).forEach(([type, typeSuggestions]) => {
      const maxScore = Math.max(...typeSuggestions.map(s => s.score))
      typeSuggestions.forEach(suggestion => {
        suggestion.normalizedScore = maxScore ? suggestion.score / maxScore : 0
        suggestion.priority =
          typePriorities[suggestion.type as indexes.RecordTypes] || 0

        if (
          suggestion.text.replace(/<\/?strong>/g, '').toLowerCase() ===
          suggestion.text.toLowerCase()
        ) {
          suggestion.priority += 3
        }

        if (suggestion.text.startsWith('<strong>')) {
          suggestion.priority += 2
        }
      })
    })
  }

  return suggestions
    .filter((suggestion, index, self) => {
      const plainText = suggestion.text.replace(/<\/?strong>/g, '')
      return (
        index ===
        self.findIndex(
          s =>
            s.text.replace(/<\/?strong>/g, '') === plainText &&
            s.type === suggestion.type
        )
      )
    })
    .sort((a, b) => {
      const priorityDiff = (b.priority || 0) - (a.priority || 0)
      if (priorityDiff !== 0) return priorityDiff

      const scoreDiff = (b.normalizedScore || 0) - (a.normalizedScore || 0)
      if (scoreDiff !== 0) return scoreDiff

      return a.text.length - b.text.length
    })
    .slice(0, 5)
}
